import type { LocalDatabaseProduct } from "../../../Data/_Bases/BaseProductRepository";
import type { LocalDatabasePromotion } from "../../../Data/_Bases/BasePromotionRepository";
import ArrayPool from "../../Generic/Pools/ArrayPool";
import ObjectPool from "../../Generic/Pools/ObjectPool";
import { Knapsack } from "./Knapsack";
import { PromotionManager } from "./PromotionManager";
import { groupProductsByUUID, type SalePromotionProduct } from "./PromotionUtilities";
import { RebateCalculation } from "./RebateCalculation";

export class RebateCalculationManager {
    constructor(
        protected manager: PromotionManager,
        protected products: Array<SalePromotionProduct>,
        protected promotions: Array<LocalDatabasePromotion>
    ) {}

    public async run(): Promise<void> {
        const grouped = groupProductsByUUID(this.products, false);
        const dbProducts = await this.manager.productRepository.getBulk(Object.keys(grouped));
        const products = ObjectPool.Get<Record<string, LocalDatabaseProduct>>();

        for(const product of dbProducts) {
            products[product.uuid] = product;
        }

        ArrayPool.Release(dbProducts);

        const matrices = ObjectPool.Get<Record<string, Array<{
            promotion: string;
            rebates: Array<number>;
        }>>>();

        for(const promotion of this.promotions) {
            const calculator = new RebateCalculation(this.manager, promotion, grouped, products);
            const matrix = calculator.runMatrix();

            for(const product in matrix) {
                if(!Object.hasOwn(matrix, product)) continue;

                if(typeof matrices[product] === "undefined") {
                    matrices[product] = ArrayPool.Get<{
                        promotion: string;
                        rebates: Array<number>;
                    }>();
                }

                const rebateObj = ObjectPool.Get<{
                    promotion: string;
                    rebates: Array<number>;
                }>();

                rebateObj.promotion = promotion.uuid;
                rebateObj.rebates = matrix[product];

                matrices[product].push(rebateObj);
            }
        }

        const rebatesToApply = ObjectPool.Get<Record<string, Array<{
            promotion: string;
            rate: number;
            remainingQuantity: number;
        }>>>();

        for(const product in matrices) {
            if(!Object.hasOwn(matrices, product)) continue;

            const knapsack = Knapsack.getObject();
            const knapsackMap = ObjectPool.Get<Record<string, number>>();

            for(const rebateOption of matrices[product]) {
                for(let i = 0, l = rebateOption.rebates.length; i < l; i++) {
                    const id = `${rebateOption.promotion}-${i}`;

                    knapsack.prices.push(rebateOption.rebates[i]);
                    knapsack.quantities.push(i + 1);
                    knapsack.maxQuantities.push(1);
                    knapsack.identifiers.push(id);

                    knapsackMap[id] = knapsack.identifiers.length - 1;
                }
            }

            const ks = Knapsack.solve(
                knapsack,
                grouped[product].total_quantity,
                false
            );

            if(ks.solution) {
                const toApply = ArrayPool.Get<{
                    promotion: string;
                    rate: number;
                    remainingQuantity: number;
                }>();

                for(let i = 0, l = ks.used.length; i < l; i++) {
                    const id = ks.used[i];

                    const application = ObjectPool.Get<{
                        promotion: string;
                        rate: number;
                        remainingQuantity: number;
                    }>();

                    const quantity = knapsack.quantities[knapsackMap[id]];

                    application.promotion = id.split("-")[0];
                    application.rate = knapsack.prices[knapsackMap[id]] / quantity;
                    application.remainingQuantity = quantity;

                    toApply.push(application);
                }

                rebatesToApply[product] = toApply;
            }

            ObjectPool.Release(ks);
        }

        // Apply the rebate to each product
        for(let i = 0, l = this.products.length; i < l; i++) {
            // Clear the old rebate
            this.products[i].rebates = ObjectPool.Get();

            // Check if we have rebates for this product
            if(typeof rebatesToApply[this.products[i].uuid] === "undefined") {
                continue;
            }

            // Get the product's quantity
            const quantity = this.products[i].quantity;

            // Apply the rebate
            for(const rebate of rebatesToApply[this.products[i].uuid]) {
                const quantityToUse = Math.min(rebate.remainingQuantity, quantity);

                if(quantityToUse <= 0) {
                    continue;
                }

                if(typeof this.products[i].rebates[rebate.promotion] === "undefined") {
                    this.products[i].rebates[rebate.promotion] = ObjectPool.Get();
                    this.products[i].rebates[rebate.promotion].amount = 0;
                    this.products[i].rebates[rebate.promotion].quantity = 0;
                }

                this.products[i].rebates[rebate.promotion].amount += rebate.rate * quantityToUse;
                this.products[i].rebates[rebate.promotion].quantity += quantityToUse;

                rebate.remainingQuantity -= quantityToUse;
            }
        }

        ObjectPool.Release(grouped);
        ObjectPool.Release(rebatesToApply);
    }
}
